import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useId,
} from 'react';
import {createPortal} from 'react-dom';
import {Plus, Search, X} from 'lucide-react';
import {useQueries} from '@tanstack/react-query';
import {cn} from '@/matic/lib/utils/cn';
import {Skeleton} from '@/matic/components/ui/skeleton';
import {Button} from '@/matic/components/ui/button';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useObjectTypes} from '@/matic/features/master-data/hooks/useObjectTypes';
import {useObjectType} from '@/matic/features/master-data/hooks/useObjectType';
import {useCreateObjectRecord} from '@/matic/features/master-data/hooks/useCreateObjectRecord';
import {ObjectRecordForm} from '@/matic/features/master-data/components/ObjectRecordForm';
import {getMasterDataApi} from '@/matic/features/master-data/api/master-data-api';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';

export type AssociationPickerSelection = {
  objectTypeId: string;
  objectTypeName: string;
  /** Null = type-only ("Category") selection: "Just {Category}", no record. */
  objectRecordId: string | null;
  objectRecordLabel: string;
};

/**
 * Build the {@link AssociationPickerProps.inlineCreate} config for the common
 * "stage the new record" case: create an ObjectRecord, then hand the resulting
 * selection back to the caller (which adds it to a staged chip list or links
 * it to an existing file). Thread classify flows have their own composite
 * endpoint and don't use this helper.
 */
export function useInlineCreateAssociation(
  onCreated: (selection: AssociationPickerSelection) => void
) {
  const createRecord = useCreateObjectRecord();

  return {
    onCreate(
      objectTypeId: string,
      objectTypeName: string,
      data: Record<string, unknown>
    ) {
      createRecord.mutate(
        {typeId: objectTypeId, data},
        {
          onSuccess(record) {
            onCreated({
              objectTypeId,
              objectTypeName,
              objectRecordId: record.id,
              objectRecordLabel:
                record.display ??
                record.code ??
                (typeof data.name === 'string' ? data.name : ''),
            });
          },
        }
      );
    },
    isPending: createRecord.isPending,
  };
}

type AssociationPickerProps = {
  onAdd: (selection: AssociationPickerSelection) => void;
  /** Called when the dropdown closes without a selection (outside click / Escape) */
  onClose?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Open the dropdown and focus the input immediately on mount */
  autoOpen?: boolean;
  /**
   * When set, the picker only surfaces object types whose id is in this list —
   * across browse groups, search hints, and the "+ New {Type}" inline-create.
   * Unset (default) lists every object type.
   */
  allowedObjectTypeIds?: string[];
  /**
   * When set, each type group surfaces a "+ New {Type}" affordance that opens
   * an inline create form (the type's own ``ObjectRecordForm``, same as every
   * other create-record surface). The caller wires ``onCreate`` to a path that
   * creates the record and stages/links it.
   */
  inlineCreate?: {
    onCreate: (
      objectTypeId: string,
      objectTypeName: string,
      data: Record<string, unknown>
    ) => void;
    isPending?: boolean;
  };
  /**
   * Offer the type-only "Just {Category}" row at the top of each group —
   * a selection with `objectRecordId: null`. Off by default so record-anchored
   * surfaces (Scout's MdmObjectPicker alias, source pickers) keep their
   * records-only contract; association surfaces (threads, notes, files) opt in.
   */
  allowCategorySelection?: boolean;
};

type RecordItem = {
  id: string;
  label: string;
  typeId: string;
  typeName: string;
};

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 260;
const MAX_WIDTH = 600;

export function AssociationPicker({
  onAdd,
  onClose,
  disabled = false,
  placeholder = 'About…',
  className,
  autoOpen = false,
  inlineCreate,
  allowCategorySelection = false,
  allowedObjectTypeIds,
}: AssociationPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  // Inline create sub-state: when set, the dropdown body shows the type's
  // record form for a new record of this type instead of the browse/search
  // results. `prefillName` seeds the form's `name` field from the search text.
  const [createCtx, setCreateCtx] = useState<{
    typeId: string;
    typeName: string;
    prefillName: string;
  } | null>(null);

  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>();
  const createFormId = useId();
  const {data: createObjectType, isLoading: isCreateTypeLoading} =
    useObjectType(createCtx?.typeId);

  const tenantId = useRequiredTenantId();
  const api = getMasterDataApi();

  const {data: objectTypesData} = useObjectTypes();
  const types = useMemo(() => {
    const all = objectTypesData?.items ?? [];
    if (!allowedObjectTypeIds) return all;
    const allowed = new Set(allowedObjectTypeIds);
    return all.filter((t) => allowed.has(t.id));
  }, [objectTypesData, allowedObjectTypeIds]);

  // Prefetch all records for every type on mount — same cache keys as the
  // rest of the app, so likely warm after any master data page visit
  const recordQueries = useQueries({
    queries: types.map((type) => ({
      queryKey: masterDataKeys.objectRecords(type.id, tenantId, undefined),
      queryFn: async () => api.listObjectRecords(type.id),
      enabled: types.length > 0,
    })),
  });

  const isLoading = recordQueries.some((q) => q.isLoading);

  const groups = useMemo(
    () =>
      types.map((type, i) => ({
        typeId: type.id,
        typeName: type.name,
        records: (recordQueries[i]?.data?.items ?? []).map(
          (r): RecordItem => ({
            id: r.id,
            typeId: type.id,
            typeName: type.name,
            label: r.display ?? r.code,
          })
        ),
      })),
    // Empty types are kept deliberately: a Category with zero records can
    // still be picked type-only ("Just {Category}") or seed its first record.
    [types, recordQueries]
  );

  // Client-side filter — instant, no debounce needed
  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    const term = search.toLowerCase();
    return (
      groups
        .map((g) => ({
          ...g,
          records: g.records.filter(
            (r) =>
              r.label.toLowerCase().includes(term) ||
              g.typeName.toLowerCase().includes(term)
          ),
        }))
        // A group whose NAME matches stays even with zero matching records —
        // it still offers the type-only "Just {Category}" row.
        .filter(
          (g) => g.records.length > 0 || g.typeName.toLowerCase().includes(term)
        )
    );
  }, [groups, search]);

  // Auto-open and focus on mount when requested
  useEffect(() => {
    if (!autoOpen) return;
    setIsOpen(true);
    // Defer focus so the input is mounted and positioned before focusing
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(id);
    };
  }, []);

  // Viewport-fixed positioning — escapes any overflow:hidden ancestor
  useLayoutEffect(() => {
    if (!isOpen) return;
    const place = () => {
      const control = controlRef.current;
      if (!control) return;
      const rect = control.getBoundingClientRect();
      const margin = 8;
      const overflowRight = rect.left + width > window.innerWidth - margin;
      const left = overflowRight
        ? Math.max(margin, window.innerWidth - margin - width)
        : rect.left;
      // Flip above when there is not enough room below — standard dropdown
      // behaviour. The anchor chip sits at the bottom of the viewport in the
      // Otto composer, so without the flip the popover goes off-screen.
      const PREFERRED_HEIGHT = 300;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const flipAbove =
        spaceBelow < PREFERRED_HEIGHT && spaceAbove > spaceBelow;

      let maxHeight: number;
      let vertical: {top: number} | {bottom: number};

      if (flipAbove) {
        maxHeight = Math.max(180, Math.min(PREFERRED_HEIGHT, spaceAbove));
        // Anchor the popover's BOTTOM edge just above the control. The popover
        // grows to its content height (not the full maxHeight), so a top-anchor
        // would leave a gap below a short dropdown — making it float away from
        // the control (e.g. when an expanded file card pushes the control low).
        vertical = {bottom: window.innerHeight - rect.top + 4};
      } else {
        vertical = {top: rect.bottom + 4};
        maxHeight = Math.max(180, spaceBelow);
      }

      setPopoverStyle((prev) => {
        const next: React.CSSProperties = {
          position: 'fixed',
          left,
          width,
          maxHeight,
          ...vertical,
        };
        if (
          prev?.top === next.top &&
          prev?.bottom === next.bottom &&
          prev?.left === next.left &&
          prev?.maxHeight === next.maxHeight &&
          prev?.width === next.width
        )
          return prev;
        return next;
      });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen, width]);

  // Close on outside click or Escape — calls onClose so the parent can
  // collapse the picker trigger (avoids useClickOutside conflicting with portal)
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (controlRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
      onClose?.();
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  // Clear search and any in-progress inline-create on close
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setCreateCtx(null);
    }
  }, [isOpen]);

  // Drag-to-resize right edge of the dropdown
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = width;

      const onMouseMove = (event: MouseEvent) => {
        const next = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, startWidth + event.clientX - startX)
        );
        setWidth(next);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [width]
  );

  const handleSelect = (record: RecordItem) => {
    onAdd({
      objectTypeId: record.typeId,
      objectTypeName: record.typeName,
      objectRecordId: record.id,
      objectRecordLabel: record.label,
    });
    setIsOpen(false);
    setSearch('');
  };

  // Type-only ("Category") selection: about this type, no specific record.
  // The default path when the user can't (or needn't) name which one —
  // rendered at the top of each group, never buried under the create action.
  const handleSelectCategory = (typeId: string, typeName: string) => {
    onAdd({
      objectTypeId: typeId,
      objectTypeName: typeName,
      objectRecordId: null,
      objectRecordLabel: typeName,
    });
    setIsOpen(false);
    setSearch('');
  };

  // Open the inline create form for a type. Prefill the name with the current
  // search text, unless that text is just a type-name filter (e.g. the user
  // clicked the "Customer" hint chip) — in which case start with a blank name.
  const startCreate = (typeId: string, typeName: string) => {
    const term = search.trim();
    const isTypeFilter = types.some(
      (t) => t.name.toLowerCase() === term.toLowerCase()
    );
    setCreateCtx({typeId, typeName, prefillName: isTypeFilter ? '' : term});
  };

  const cancelCreate = () => {
    setCreateCtx(null);
  };

  const submitCreate = (data: Record<string, unknown>) => {
    if (!createCtx || !inlineCreate || inlineCreate.isPending) return;
    inlineCreate.onCreate(createCtx.typeId, createCtx.typeName, data);
    setCreateCtx(null);
    setIsOpen(false);
    setSearch('');
  };

  const showHints = !search && !isLoading;
  // Without category selection, a group with zero matching records has
  // nothing to offer — drop it so "No matches" stays truthful.
  const visibleGroups = allowCategorySelection
    ? filteredGroups
    : filteredGroups.filter((g) => g.records.length > 0);
  const hasResults = visibleGroups.length > 0;
  // Same rule for the "Search across" chips: without category selection, a
  // chip for a zero-record Category filters to a group `visibleGroups` then
  // removes — a dead-end "No matches" with no way forward.
  const hintGroups = allowCategorySelection
    ? groups
    : groups.filter((g) => g.records.length > 0);
  const createFooterGroups =
    inlineCreate && !createCtx && !showHints && hasResults ? visibleGroups : [];

  return (
    <div ref={controlRef} className={cn('relative min-w-0 flex-1', className)}>
      {/* Single bordered control — input IS the trigger (AssigneePicker pattern) */}
      <div
        className={cn(
          'rounded-md border bg-white shadow-sm transition-colors',
          isOpen
            ? 'border-primary-500 ring-1 ring-primary-500'
            : 'border-neutral-300 hover:border-neutral-400',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        <div
          className="flex h-8 cursor-text items-center gap-2 px-3"
          onClick={() => {
            setIsOpen((o) => !o);
            inputRef.current?.focus();
          }}
        >
          <Search
            className="h-3.5 w-3.5 shrink-0 text-neutral-400"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            placeholder={placeholder}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            role="combobox"
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSearch('');
                inputRef.current?.focus();
              }}
              className="shrink-0 text-neutral-400 hover:text-neutral-600"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Portal dropdown — escapes panel overflow clipping */}
      {isOpen &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            style={popoverStyle}
            className="relative z-[60] flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
          >
            <div className="min-h-0 flex-1 overflow-y-auto" role="listbox">
              {isLoading ? (
                <div className="space-y-1 p-2">
                  {Array.from({length: 5}).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-full" />
                  ))}
                </div>
              ) : createCtx ? (
                <div
                  className="p-3"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelCreate();
                    }
                  }}
                >
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                    New {createCtx.typeName}
                  </p>
                  {isCreateTypeLoading || !createObjectType ? (
                    <div className="space-y-1.5">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-2/3" />
                    </div>
                  ) : (
                    <ObjectRecordForm
                      objectType={createObjectType}
                      formId={createFormId}
                      mode="create"
                      collapseOptionalFields
                      initialData={
                        createCtx.prefillName
                          ? {name: createCtx.prefillName}
                          : undefined
                      }
                      onValidSubmit={(payload) => {
                        submitCreate(payload.data);
                      }}
                    />
                  )}
                  <div className="mt-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelCreate}
                      className="rounded px-2 py-1 text-xs font-medium text-neutral-500 transition-colors hover:text-neutral-700"
                    >
                      Cancel
                    </button>
                    <Button
                      type="submit"
                      form={createFormId}
                      size="sm"
                      variant="dark"
                      disabled={
                        isCreateTypeLoading ||
                        !createObjectType ||
                        inlineCreate?.isPending
                      }
                      loading={inlineCreate?.isPending}
                    >
                      Create
                    </Button>
                  </div>
                </div>
              ) : showHints ? (
                <div className="px-3 py-3">
                  <p className="mb-2 text-xs text-neutral-400">Search across</p>
                  <div className="flex flex-wrap gap-1.5">
                    {hintGroups.map((g) => {
                      const isActive =
                        search.toLowerCase() === g.typeName.toLowerCase();
                      return (
                        <button
                          key={g.typeId}
                          type="button"
                          onClick={() => {
                            setSearch(g.typeName);
                            inputRef.current?.focus();
                          }}
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs transition-colors',
                            isActive
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                          )}
                        >
                          {g.typeName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : hasResults ? (
                <div className="py-1">
                  {visibleGroups.map((group) => (
                    <div key={group.typeId}>
                      <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                        {group.typeName}
                      </div>
                      {allowCategorySelection && (
                        <button
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="w-full px-3 py-1.5 pl-5 text-left text-sm italic text-neutral-500 transition-colors hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => {
                            handleSelectCategory(group.typeId, group.typeName);
                          }}
                        >
                          Just {group.typeName} — no specific tag
                        </button>
                      )}
                      {group.records.map((record) => (
                        <button
                          key={record.id}
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="w-full px-3 py-1.5 pl-5 text-left text-sm text-neutral-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => {
                            handleSelect(record);
                          }}
                        >
                          {record.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-neutral-400">
                  No matches
                </div>
              )}
            </div>

            {createFooterGroups.length > 0 && (
              <div className="shrink-0 border-t border-neutral-200 bg-white p-1.5">
                {createFooterGroups.map((group) => (
                  <button
                    key={group.typeId}
                    type="button"
                    onClick={() => {
                      startCreate(group.typeId, group.typeName);
                    }}
                    className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50"
                  >
                    <Plus className="h-3 w-3 shrink-0" />
                    New {group.typeName}
                  </button>
                ))}
              </div>
            )}

            {/* Right-edge drag handle */}
            <div
              className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize transition-colors hover:bg-primary-200"
              onMouseDown={handleResizeMouseDown}
              title="Drag to resize"
            />
          </div>,
          document.body
        )}
    </div>
  );
}
